/**
 * riverPathsPipeline — Touch draws river paths that erode the landscape.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface RiverPathsPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  uPointer: WebGLUniformLocation | null;
  uPointerActive: WebGLUniformLocation | null;
  uPointerVel: WebGLUniformLocation | null;
  uClickPos: WebGLUniformLocation | null;
  uClickAge: WebGLUniformLocation | null;
  bands: Float32Array;
  smoothX: number;
  smoothY: number;
  prevX: number;
  prevY: number;
  clicks: { x: number; y: number; t: number }[];
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
uniform vec2 uPointer;
uniform float uPointerActive;
uniform vec2 uPointerVel;
uniform vec4 uClickPos;
uniform vec4 uClickAge;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.03 + 7.1;
    a *= 0.5;
  }
  return v;
}


void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = vec3(0.10, 0.12, 0.08);
  vec3 col = bg;

  vec2 pPos = (uPointer - 0.5 * uResolution) / uResolution.y;
  pPos.y = -pPos.y;
  vec2 toP = pPos - p;
  float pDist = length(toP);

  float clickE = 0.0;
  for (int i = 0; i < 2; i++) {
    vec2 cp = vec2(uClickPos[i*2], uClickPos[i*2+1]);
    float ca = uClickAge[i];
    if (ca >= 0.0 && ca < 4.0 && cp.x >= 0.0) {
      vec2 cPos = (cp - 0.5 * uResolution) / uResolution.y;
      cPos.y = -cPos.y;
      float cd = length(p - cPos);
      float radius = ca * 0.20;
      float band = exp(-pow((cd - radius) * 10.0, 2.0));
      float fade = 1.0 / (1.0 + ca * 1.5);
      clickE += band * fade;
    }
  }

  // A landscape with multiple meandering rivers.
  // The terrain has elevation, the rivers are blue.
  // The rivers are sinuous paths that erode the landscape.
  // Compute the height field for the terrain.
  float terrain = fbm(p * 1.5) * 0.4;
  terrain += fbm(p * 4.0) * 0.15;
  // Color: green grass with elevation tint.
  vec3 grass = vec3(0.30, 0.40, 0.20);
  grass = mix(grass, vec3(0.50, 0.55, 0.30), smoothstep(0.0, 0.3, terrain));
  grass = mix(grass, vec3(0.70, 0.65, 0.50), smoothstep(0.3, 0.6, terrain));
  grass;
  // 4 main rivers.
  for (int ri = 0; ri < 4; ri++) {
    float fr = float(ri);
    for (int k = 0; k < 60; k++) {
      float fk = float(k) / 59.0;
      float along = fk * 1.5 - 0.75;
      vec2 pt = vec2(
        along,
        0.0
      );
      // Meander.
      pt.y = sin(along * 2.0 + fr * 1.3 + uTime * 0.2) * 0.20 + cos(along * 4.0 + fr * 2.1) * 0.10;
      pt.x += sin(along * 3.0 + fr) * 0.05;
      // Offset vertically so rivers are at different y positions.
      pt.y += (fr - 1.5) * 0.20;
      float d = length(p - pt);
      // River width.
      float width = 0.020 + 0.005 * sin(along * 5.0 + fr);
      if (d < width) {
        // River color.
        vec3 riverCol = vec3(0.20, 0.50, 0.75);
        // Shimmer.
        riverCol += vec3(0.85, 0.95, 1.0) * 0.3 * (0.5 + 0.5 * sin(along * 10.0 + uTime));
        col = mix(col, riverCol, (1.0 - d / width) * 0.95);
      }
    }
  }
  // Tributaries: smaller streams flowing into the main rivers.
  for (int ti = 0; ti < 6; ti++) {
    float ft = float(ti);
    for (int k = 0; k < 30; k++) {
      float fk = float(k) / 29.0;
      float along = fk * 0.8;
      vec2 pt = vec2(along - 0.4 + sin(ft * 2.0) * 0.2, (ft - 3.0) * 0.20 + cos(along * 3.0 + ft) * 0.10);
      float d = length(p - pt);
      if (d < 0.012) {
        col = mix(col, vec3(0.20, 0.50, 0.75), 0.7);
      }
    }
  }
  // Trees scattered.
  for (int ti = 0; ti < 15; ti++) {
    float ft = float(ti);
    vec2 tpos = vec2(
      hash21(vec2(ft, 1.0)) * 1.6 - 0.8,
      hash21(vec2(ft, 2.0)) * 0.9 - 0.4
    );
    float td = length(p - tpos);
    if (td < 0.020) {
      col = mix(col, vec3(0.20, 0.45, 0.15), 0.7);
    }
  }
  // Touch draws a new river.
  if (uPointerActive > 0.001) {
    for (int k = 0; k < 30; k++) {
      float fk = float(k) / 29.0;
      float along = fk * 0.30;
      vec2 pt = pPos + vec2(along, sin(along * 3.0 + uTime) * 0.05);
      float d = length(p - pt);
      if (d < 0.012) {
        col = mix(col, vec3(0.20, 0.50, 0.75), 0.8 * uPointerActive);
      }
    }
  }
  // Click = a river expands from the click.
  for (int i = 0; i < 2; i++) {
    if (uClickPos[i*2] >= 0.0) {
      vec2 cp = vec2(uClickPos[i*2], uClickPos[i*2+1]);
      vec2 cPos = (cp - 0.5 * uResolution) / uResolution.y;
      cPos.y = -cPos.y;
      float age = uClickAge[i];
      for (int k = 0; k < 30; k++) {
        float fk = float(k) / 29.0;
        float along = fk * age * 0.4;
        vec2 pt = cPos + vec2(along, sin(along * 3.0) * 0.06);
        float d = length(p - pt);
        if (d < 0.014) {
          col = mix(col, vec3(0.20, 0.50, 0.75), (1.0 - age * 0.3) * 0.9);
        }
      }
    }
  }
  col += vec3(0.4, 0.7, 0.95) * clickE * 0.5;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeRiverPathsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeRiverPathsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createRiverPathsPipeline(gl: WebGL2RenderingContext): RiverPathsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    uPointer: gl.getUniformLocation(program, "uPointer"),
    uPointerActive: gl.getUniformLocation(program, "uPointerActive"),
    uPointerVel: gl.getUniformLocation(program, "uPointerVel"),
    uClickPos: gl.getUniformLocation(program, "uClickPos"),
    uClickAge: gl.getUniformLocation(program, "uClickAge"),
    bands: new Float32Array(9),
    smoothX: 0,
    smoothY: 0,
    prevX: 0,
    prevY: 0,
    clicks: [],
  };
}

export function riverPathsUpdatePointer(
  pipe: RiverPathsPipeline,
  x: number,
  y: number,
  active: boolean,
  now: number,
): void {
  const dx = x - pipe.prevX;
  const dy = y - pipe.prevY;
  const vx = (pipe as any)._velX ?? 0;
  const vy = (pipe as any)._velY ?? 0;
  (pipe as any)._velX = vx + (dx - vx) * 0.30;
  (pipe as any)._velY = vy + (dy - vy) * 0.30;
  pipe.prevX = x;
  pipe.prevY = y;
  pipe.smoothX += (x - pipe.smoothX) * 0.20;
  pipe.smoothY += (y - pipe.smoothY) * 0.20;
  const target = active ? 1 : 0;
  (pipe as any)._active = ((pipe as any)._active ?? 0) + (target - ((pipe as any)._active ?? 0)) * 0.15;
}

export function riverPathsPushClick(
  pipe: RiverPathsPipeline,
  x: number, y: number, now: number,
): void {
  pipe.clicks.push({ x, y, t: now });
  if (pipe.clicks.length > 2) pipe.clicks.shift();
}

export function drawRiverPaths(
  gl: WebGL2RenderingContext,
  pipe: RiverPathsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const cp = [0, 0, 0, 0];
  const ca = [0, 0, 0, 0];
  const c0 = pipe.clicks[0];
  const c1 = pipe.clicks[1];
  if (c0) {
    const age = t - c0.t;
    if (age < 4.0) { cp[0] = c0.x; cp[1] = c0.y; ca[0] = age; }
    else { cp[0] = -1; ca[0] = 4.0; }
  } else { cp[0] = -1; ca[0] = 4.0; }
  if (c1) {
    const age = t - c1.t;
    if (age < 4.0) { cp[2] = c1.x; cp[3] = c1.y; ca[1] = age; }
    else { cp[2] = -1; ca[1] = 4.0; }
  } else { cp[2] = -1; ca[1] = 4.0; }
  if ((pipe as any)._active === undefined) (pipe as any)._active = 0;
  gl.uniform2f(pipe.uResolution!, width, height);
  gl.uniform1f(pipe.uTime!, t);
  gl.uniform3f(pipe.uBass!, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(pipe.uMid!, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(pipe.uHigh!, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.uniform2f(pipe.uPointer!, pipe.smoothX, pipe.smoothY);
  gl.uniform1f(pipe.uPointerActive!, (pipe as any)._active);
  gl.uniform2f(pipe.uPointerVel!, (pipe as any)._velX || 0, (pipe as any)._velY || 0);
  gl.uniform4f(pipe.uClickPos!, cp[0], cp[1], cp[2], cp[3]);
  gl.uniform4f(pipe.uClickAge!, ca[0], ca[1], ca[2], ca[3]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
